export const ENTITY = "TOXIC STREET LLC";
export const JURISDICTION = "State of New York";
export const CONTACT_EMAIL = "legal@toxicstreet.co";
export const LAST_UPDATED = "February 2026";
export const EFFECTIVE_DATE = "February 2026";

export const LEGAL_ORDER = [
  { slug: "privacy",       label: "Privacy Policy" },
  { slug: "terms",         label: "Terms of Service" },
  { slug: "cookies",       label: "Cookie Notice" },
  { slug: "shipping",      label: "Shipping Policy" },
  { slug: "returns",       label: "Returns & Refunds" },
  { slug: "accessibility", label: "Accessibility" },
];

export const LEGAL = {
  privacy: {
    title: "Privacy Policy",
    summary: `This Privacy Policy describes how ${ENTITY} ("we", "us", or "TOXIC STREET") collects, uses, and discloses information when you visit toxicstreet.co or purchase our products. By accessing the site you consent to the practices set forth below.`,
    sections: [
      {
        n: "01", id: "information-we-collect", h: "Information We Collect",
        body: [
          "We collect information you voluntarily provide at checkout (name, shipping address, email) and information automatically generated by your interaction with the site (IP address, device type, browser, referring URL, timestamp).",
          "Payment information is collected and processed directly by Stripe, Inc. We do not store full card numbers or CVC codes on our servers."
        ]
      },
      {
        n: "02", id: "use-of-information", h: "How We Use Information",
        body: [
          "Information collected is used to (i) fulfill and ship orders, (ii) communicate order status and customer-service correspondence, (iii) prevent fraud, abuse, and chargebacks, (iv) comply with legal obligations, and (v) — only with separate opt-in — send marketing communications about future drops.",
          "We do not sell, rent, or trade personal data to third parties for their independent marketing."
        ]
      },
      {
        n: "03", id: "service-providers", h: "Service Providers",
        body: [
          "We share data with vetted processors strictly to provide the service: Stripe (payments), shipping carriers (delivery), Cloudflare (hosting and DDoS protection), and email-delivery providers. Each is bound by contract to confidentiality and security standards consistent with this Policy."
        ]
      },
      {
        n: "04", id: "retention", h: "Retention",
        body: [
          "Order records are retained for a minimum of seven (7) years to comply with U.S. tax and consumer-protection law. Marketing data is retained until you opt out. You may request deletion of personal data not subject to a retention obligation by writing to the address below."
        ]
      },
      {
        n: "05", id: "your-rights", h: "Your Rights",
        body: [
          "Residents of California, the EEA, the United Kingdom, and certain other jurisdictions have statutory rights of access, correction, portability, restriction, and erasure with respect to their personal data. To exercise any such right, contact us at " + CONTACT_EMAIL + ". We will respond within the period required by applicable law (typically thirty to forty-five days)."
        ]
      },
      {
        n: "06", id: "children", h: "Children Under 13",
        body: [
          "TOXIC STREET does not knowingly collect personal information from children under thirteen (13). If you believe a child has provided us information, please contact us and we will delete it."
        ]
      },
      {
        n: "07", id: "changes", h: "Changes to This Policy",
        body: [
          "We may amend this Policy from time to time. Material changes will be reflected by an updated Effective Date. Continued use of the site after such changes constitutes acceptance."
        ]
      }
    ]
  },

  terms: {
    title: "Terms of Service",
    summary: `These Terms of Service ("Terms") govern your access to and use of the website operated by ${ENTITY} and the purchase of any products offered therein. By placing an order or otherwise using the site, you agree to be bound by these Terms.`,
    sections: [
      {
        n: "01", id: "eligibility", h: "Eligibility",
        body: [
          "The site is intended for individuals thirteen (13) years of age and older. Customers under the age of majority in their jurisdiction must obtain the consent of a parent or legal guardian before placing an order."
        ]
      },
      {
        n: "02", id: "orders", h: "Orders, Pricing, and Payment",
        body: [
          "All prices are quoted in U.S. dollars and are exclusive of applicable sales or value-added tax, which will be added at checkout where required by law. We reserve the right to refuse or cancel any order, in our sole discretion, including for reasons of suspected fraud, error in pricing or inventory, or violation of these Terms.",
          "Payment is processed by Stripe, Inc. By submitting payment information you represent that you are authorized to use the payment method and authorize us (or our processor) to charge the total amount of your order."
        ]
      },
      {
        n: "03", id: "shipping-risk", h: "Shipping and Risk of Loss",
        body: [
          "Title and risk of loss for products pass to you upon transfer to the carrier. Estimated delivery windows are provided in good faith but are not guaranteed. Please refer to the Shipping Policy and Returns & Refunds page for further detail."
        ]
      },
      {
        n: "04", id: "ip", h: "Intellectual Property",
        body: [
          `All content displayed on the site — including graphic artwork, photography, typography, source code, and the marks "TOXIC STREET", "TOXIC/STREET", "DISCIPLINE OVER EVERYTHING", and "STAY DANGEROUS" — is the exclusive property of ${ENTITY} or its licensors and is protected by U.S. and international intellectual-property law. No portion may be reproduced, modified, or commercially exploited without prior written permission.`
        ]
      },
      {
        n: "05", id: "prohibited", h: "Prohibited Conduct",
        body: [
          "You agree not to (a) interfere with or disrupt the site, (b) attempt to gain unauthorized access to any account or system, (c) scrape, harvest, or aggregate data without our written consent, (d) resell products purchased through the site for commercial purposes without our written consent, or (e) use the site to engage in any unlawful activity."
        ]
      },
      {
        n: "06", id: "disclaimer", h: "Disclaimer of Warranties",
        body: [
          'THE SITE AND ALL PRODUCTS ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.'
        ]
      },
      {
        n: "07", id: "liability", h: "Limitation of Liability",
        body: [
          "TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT WILL " + ENTITY.toUpperCase() + ", ITS OFFICERS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR EXEMPLARY DAMAGES, INCLUDING LOST PROFITS OR REVENUES, ARISING OUT OF OR IN CONNECTION WITH THESE TERMS OR YOUR USE OF THE SITE. OUR AGGREGATE LIABILITY FOR ANY CLAIM ARISING UNDER OR RELATING TO THESE TERMS SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT PAID BY YOU TO US IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS ($100)."
        ]
      },
      {
        n: "08", id: "governing-law", h: "Governing Law and Dispute Resolution",
        body: [
          `These Terms are governed by the laws of the ${JURISDICTION}, without regard to its conflict-of-law principles. Any dispute arising hereunder shall be brought exclusively in the state or federal courts located in New York County, New York, and you irrevocably consent to the jurisdiction of such courts.`
        ]
      },
      {
        n: "09", id: "contact", h: "Contact",
        body: [
          `Questions concerning these Terms may be directed to ${ENTITY} at ${CONTACT_EMAIL}.`
        ]
      }
    ]
  },

  cookies: {
    title: "Cookie Notice",
    summary: `This Cookie Notice explains how ${ENTITY} uses cookies and similar local-storage technologies when you interact with our website.`,
    sections: [
      {
        n: "01", id: "what-cookies", h: "What Are Cookies",
        body: [
          'Cookies are small text files placed on your device by a website you visit. "Local storage" and "session storage" are similar technologies provided by your browser that allow data to persist across page loads.'
        ]
      },
      {
        n: "02", id: "categories", h: "Categories We Use",
        body: [
          "Strictly Necessary — cart contents (toxic_street_cart_v1), administrative session tokens (ts_admin_token), and a one-time boot flag (ts_booted). The site cannot function correctly without these.",
          "Functional — drop countdown reference timestamp (ts_drop_target) and last-order tracking convenience values (ts_last_order_id, ts_last_order_email).",
          "We do not use third-party advertising or cross-site tracking cookies at this time."
        ]
      },
      {
        n: "03", id: "managing", h: "Managing Cookies",
        body: [
          'Most browsers allow you to view, manage, or block cookies through their settings. Disabling Strictly Necessary cookies may prevent core features (checkout, cart, admin) from functioning. For a "clean" session, use a Private/Incognito window.'
        ]
      }
    ]
  },

  shipping: {
    title: "Shipping Policy",
    summary: "All TOXIC STREET orders are picked, packed, and shipped in small batches from our fulfillment partner. Orders placed before 12:00 p.m. Eastern Time on a business day are typically processed the same day; orders placed thereafter are processed the next business day.",
    sections: [
      {
        n: "01", id: "rates", h: "Rates",
        body: [
          "Standard shipping within the contiguous United States is a flat eight U.S. dollars ($8). Orders with a subtotal of one hundred U.S. dollars ($100) or more ship free.",
          "International rates are calculated at checkout based on destination and weight. Recipients are responsible for any import duties, taxes, or customs fees levied by the destination country."
        ]
      },
      {
        n: "02", id: "transit", h: "Transit Times",
        body: [
          "Domestic: 3–7 business days from ship date. International: 7–21 business days. These windows are estimates provided by the carrier and are not guaranteed."
        ]
      },
      {
        n: "03", id: "lost", h: "Lost, Stolen, or Damaged Packages",
        body: [
          "Once a package is marked delivered by the carrier, risk of loss has passed to you. We are happy to assist with carrier claims; please contact us within seven (7) days of the delivery scan. Packages damaged in transit will be replaced at no cost upon receipt of photographic evidence."
        ]
      }
    ]
  },

  returns: {
    title: "Returns & Refunds",
    summary: "We stand behind every piece we drop. If something isn't right, we'll make it right — within the windows set out below.",
    sections: [
      {
        n: "01", id: "window", h: "Return Window",
        body: [
          "Unworn, unwashed merchandise in its original condition may be returned within fourteen (14) days of the delivery date for a refund to the original payment method, less original shipping. Items returned outside this window will be declined."
        ]
      },
      {
        n: "02", id: "process", h: "How to Initiate a Return",
        body: [
          "Email " + CONTACT_EMAIL + " with your order number and the item(s) you wish to return. We will issue a prepaid return label by email within two (2) business days. A flat seven-dollar ($7) return-shipping fee will be deducted from your refund."
        ]
      },
      {
        n: "03", id: "exclusions", h: "Final-Sale Items",
        body: [
          "Items marked Final Sale, items from collaborative or limited-edition drops, and items sold at greater than thirty percent (30%) off are not eligible for return."
        ]
      },
      {
        n: "04", id: "refund-timing", h: "Refund Timing",
        body: [
          "Refunds are processed within five (5) business days of receipt of the returned merchandise. Your bank or card issuer may require an additional 3–10 business days to post the credit."
        ]
      }
    ]
  },

  accessibility: {
    title: "Accessibility Statement",
    summary: `${ENTITY} is committed to ensuring that toxicstreet.co is accessible to and usable by people with disabilities. We seek to conform to the Web Content Accessibility Guidelines (WCAG) 2.1, Level AA.`,
    sections: [
      {
        n: "01", id: "ongoing", h: "Ongoing Effort",
        body: [
          "We test the site with keyboard-only navigation, screen-reader compatibility, sufficient color contrast, and semantic markup. Accessibility is an ongoing process, and we work to remediate issues promptly as they are identified."
        ]
      },
      {
        n: "02", id: "feedback", h: "Feedback",
        body: [
          "If you encounter an accessibility barrier on the site or require an alternative means of accessing any content, please contact " + CONTACT_EMAIL + " and we will respond within five (5) business days. Your feedback helps us improve."
        ]
      }
    ]
  }
};
